import MOCK from "./businessBranding.mock.json";
import { tokensForTheme, presetById, DEFAULT_PRESET_ID } from "./palette";

/* ------------------------------------------------------------------ *
 * Per-business branding: colour theme + logo.
 *
 * Source of truth is the server (Business.branding, see ./brandingApi.js):
 * every response is primed in here with primeBranding(). Underneath sit
 * the built-in defaults in businessBranding.mock.json — the bundled logos
 * and palettes the shipped businesses wear until someone saves their own.
 * The read side is synchronous so brandFor(), the print pages and the
 * :root theme can all resolve a business without a loading state.
 *
 * Shape (per business):
 *   {
 *     theme: { preset: "heritage-gold" | … | "custom",
 *              primary: "#rrggbb" | null, accent: …, header: … },
 *     logo:  { url: dataURL/https URL | null,   // uploaded artwork
 *              asset: "raysflorist" | null,     // bundled artwork key
 *              height: 36,                      // top bar, px
 *              print_height: 44 }               // print pages, px
 *   }
 * ------------------------------------------------------------------ */

const norm = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/* business objects arrive in three shapes across the app */
const idOf = (b) => (b ? b.id || b.business_id || null : null);
const nameOf = (b) => (b ? b.name || b.business_name || null : null);
const codeOf = (b) => (b ? b.code || b.business_code || null : null);

/* docs primed from the server win over the built-in defaults */
const primed = {};

const listeners = new Set();
let version = 0;
function emit() {
  version += 1;
  cache.clear();
  listeners.forEach((fn) => fn());
}

export function subscribeBranding(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export const brandingVersion = () => version;

/* ---------- resolution ---------- */

function mockFor(business) {
  const id = idOf(business);
  const keys = [norm(nameOf(business)), norm(codeOf(business))].filter(Boolean);
  const list = MOCK.workspaces || [];
  return (
    (id && list.find((w) => w.business_id && String(w.business_id) === String(id))) ||
    list.find(
      (w) => keys.indexOf(norm(w.business_name)) !== -1 || keys.indexOf(norm(w.business_code)) !== -1
    ) ||
    null
  );
}

const keyFor = (business) => {
  const id = idOf(business);
  if (id) return String(id);
  const n = norm(codeOf(business) || nameOf(business));
  return n ? `n:${n}` : null;
};

function merge(base, patch) {
  if (!patch) return base;
  return {
    theme: { ...base.theme, ...(patch.theme || {}) },
    logo: { ...base.logo, ...(patch.logo || {}) },
  };
}

const cache = new Map();

/**
 * The resolved branding for a business (or the default when null):
 *   { theme, logo, tokens, isCustomised, isOwn }
 * Stable object per business per store version, so it is safe in deps.
 */
export function getBranding(business) {
  // the built-in defaults match on name/code, so they are part of the cache key
  const key = `${keyFor(business) || "__default"}|${norm(nameOf(business))}|${norm(codeOf(business))}`;
  if (cache.has(key)) return cache.get(key);

  const def = MOCK.default;
  const mock = business ? mockFor(business) : null;
  let doc = merge(def, mock && mock.branding);
  const k = keyFor(business);
  if (k && primed[k]) doc = merge(doc, primed[k]);

  if (!presetById(doc.theme.preset) && doc.theme.preset !== "custom") {
    doc = { ...doc, theme: { ...doc.theme, preset: DEFAULT_PRESET_ID } };
  }

  const out = {
    theme: doc.theme,
    logo: doc.logo,
    tokens: tokensForTheme(doc.theme),
    /* saved on the server, so Reset has something to drop */
    isCustomised: !!(k && primed[k]),
    /* false when nothing (built-in default or server) names this business —
       it is wearing the default palette, not one it picked */
    isOwn: !!(mock || (k && primed[k])),
  };
  cache.set(key, out);
  return out;
}

/** Seed the synchronous layer from a server response; null drops it
    (the business is back on its built-in default). */
export function primeBranding(business, doc) {
  const k = keyFor(business);
  if (!k) return;
  const had = !!primed[k];
  if (doc) primed[k] = { theme: doc.theme || {}, logo: doc.logo || {} };
  else delete primed[k];
  if (doc || had) emit();
}
